import { defineComponent as _defineComponent } from 'vue';
import { library } from "@fortawesome/fontawesome-svg-core";
import { faCheckSquare, faSquare } from "@fortawesome/free-regular-svg-icons";
import { faArrowCircleDown, faArrowCircleUp, faCheckCircle, faExchangeAlt, faSpinner, } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { BBadge, BButton, BCollapse } from "bootstrap-vue";
import { computed, ref } from "vue";
import { useRoute, useRouter } from "vue-router/composables";
import { updateContentFields } from "@/components/History/model/queries";
import { useEntryPointStore } from "@/stores/entryPointStore";
import { useEventStore } from "@/stores/eventStore";
import { clearDrag } from "@/utils/setDrag";
import { JobStateSummary } from "./Collection/JobStateSummary";
import { HIERARCHICAL_COLLECTION_JOB_STATES, STATES } from "./model/states";
import CollectionDescription from "./Collection/CollectionDescription.vue";
import ContentOptions from "./ContentOptions.vue";
import DatasetDetails from "./Dataset/DatasetDetails.vue";
import StatelessTags from "@/components/TagsMultiselect/StatelessTags.vue";
export default /*#__PURE__*/ _defineComponent({
    __name: 'ContentItem',
    props: {
        id: null,
        item: null,
        name: null,
        expandDataset: { type: Boolean, default: false },
        writable: { type: Boolean, default: true },
        addHighlightBtn: { type: Boolean, default: false },
        highlight: { default: undefined },
        isDataset: { type: Boolean, default: true },
        isRangeSelectAnchor: { type: Boolean, default: false },
        isHistoryItem: { type: Boolean, default: false },
        selected: { type: Boolean, default: false },
        selectable: { type: Boolean, default: false },
        filterable: { type: Boolean, default: false },
        isPlaceholder: { type: Boolean, default: false },
        isSubItem: { type: Boolean, default: false }
    },
    emits: ["update:selected", "update:expand-dataset", "shift-arrow-select", "init-key-selection", "arrow-navigate", "hide-selection", "select-all", "selected-to", "delete", "undelete", "unhide", "view-collection", "drag-start", "tag-change", "tag-click", "toggleHighlights"],
    setup(__props, { emit }) {
        const props = __props;
        library.add(faArrowCircleUp, faArrowCircleDown, faCheckCircle, faExchangeAlt, faSpinner);
        const router = useRouter();
        const route = useRoute();
        const entryPointStore = useEntryPointStore();
        const eventStore = useEventStore();
        const contentItem = ref(null);
        const subItemsVisible = ref(false);
        const jobState = computed(() => {
            return new JobStateSummary(props.item);
        });
        const contentId = computed(() => {
            return `dataset-${props.item.id}`;
        });
        const contentCls = computed(() => {
            const status = contentState.value && contentState.value.status;
            if (props.item.accessible === false) {
                return "alert-inaccessible";
            }
            else if (props.selected) {
                return "alert-info";
            }
            else if (!status) {
                return `alert-success`;
            }
            else {
                return `alert-${status}`;
            }
        });
        const computedClass = computed(() => {
            return {
                "content-item m-1 p-0 rounded btn-transparent-background": true,
                [contentCls.value]: true,
                "being-used": Object.values(itemUrls.value).includes(route.path),
                "range-select-anchor": props.isRangeSelectAnchor,
                "sub-item": props.isSubItem,
            };
        });
        const contentState = computed(() => {
            return STATES[state.value] && STATES[state.value];
        });
        const hasTags = computed(() => {
            return tags.value && tags.value.length > 0;
        });
        const hasStateIcon = computed(() => {
            return contentState.value && contentState.value.icon;
        });
        const state = computed(() => {
            if (props.isPlaceholder) {
                return "placeholder";
            }
            if (props.item.accessible === false) {
                return "inaccessible";
            }
            if (props.item.populated_state === "failed") {
                return "failed_populated_state";
            }
            if (props.item.populated_state === "new") {
                return "new_populated_state";
            }
            if (props.item.job_state_summary) {
                for (const jobState of HIERARCHICAL_COLLECTION_JOB_STATES) {
                    if (props.item.job_state_summary[jobState] > 0) {
                        return jobState;
                    }
                }
            }
            else if (props.item.state) {
                return props.item.state;
            }
            return "ok";
        });
        const dataState = computed(() => {
            if (props.item.accessible === false) {
                return "inaccessible";
            }
            else if (state.value === "new_populated_state") {
                return "new";
            }
            else {
                return state.value;
            }
        });
        const tags = computed(() => {
            return props.item.tags;
        });
        const tagsDisabled = computed(() => {
            return !props.writable || !props.expandDataset || !props.isHistoryItem;
        });
        const isCollection = computed(() => {
            return "collection_type" in props.item;
        });
        const itemUrls = computed(() => {
            const id = props.item.id;
            if (isCollection.value) {
                return {
                    edit: `/collection/${id}/edit`,
                    showDetails: props.item.job_source_id && props.item.job_source_type === "Job"
                        ? `/jobs/${props.item.job_source_id}/view`
                        : null,
                };
            }
            return {
                display: `/datasets/${id}/preview`,
                edit: `/datasets/${id}/edit`,
                showDetails: `/datasets/${id}/details`,
                reportError: `/datasets/${id}/error`,
                rerun: `/tool_runner/rerun?id=${id}`,
                visualize: `/visualizations?dataset_id=${id}`,
            };
        });
        /** Based on the user's keyboard platform, checks if it is the
         * typical key for selection (ctrl for windows/linux, cmd for mac)
         */
        function isSelectKey(event) {
            return eventStore.isMac ? event.metaKey : event.ctrlKey;
        }
        function onKeyDown(event) {
            var _a;
            const classList = (_a = event.target) === null || _a === void 0 ? void 0 : _a.classList;
            if (!classList.contains("content-item") || classList.contains("sub-item")) {
                return;
            }
            if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                onClick();
            }
            else if ((event.key === "ArrowUp" || event.key === "ArrowDown") && !event.shiftKey) {
                event.preventDefault();
                emit("arrow-navigate", event.key);
            }
            if (props.writable) {
                if (event.key === "Tab") {
                    emit("init-key-selection");
                }
                else {
                    event.preventDefault();
                    if ((event.key === "ArrowUp" || event.key === "ArrowDown") && event.shiftKey) {
                        emit("shift-arrow-select", event.key);
                    }
                    else if (event.key === "ArrowUp" || event.key === "ArrowDown") {
                        emit("init-key-selection");
                    }
                    else if (event.key === "Delete" && !props.selected && !props.item.deleted) {
                        onDelete(event.shiftKey);
                        emit("arrow-navigate", "ArrowDown");
                    }
                    else if (event.key === "Escape") {
                        emit("hide-selection");
                    }
                    else if (event.key === "a" && isSelectKey(event)) {
                        emit("select-all");
                    }
                }
            }
        }
        function onClick(e) {
            const event = e;
            if (event && props.writable) {
                if (isSelectKey(event)) {
                    emit("init-key-selection");
                    emit("update:selected", !props.selected);
                    return;
                }
                else if (event.shiftKey) {
                    emit("selected-to");
                    return;
                }
                else {
                    emit("init-key-selection");
                }
            }
            if (props.isPlaceholder) {
                return;
            }
            if (props.isDataset) {
                emit("update:expand-dataset", !props.expandDataset);
            }
            else {
                emit("view-collection", props.item, props.name);
            }
        }
        function onDisplay() {
            var _a;
            const entryPointsForHda = entryPointStore.entryPointsForHda(props.item.id);
            if (entryPointsForHda && entryPointsForHda.length > 0) {
                // there can be more than one entry point, choose the first
                const url = (_a = entryPointsForHda[0]) === null || _a === void 0 ? void 0 : _a.target;
                window.open(url, "_blank");
            }
            else {
                // vue-router 4 supports a native force push with clean URLs,
                // but we're using a __vkey__ bit as a workaround
                // Only conditionally force to keep urls clean most of the time.
                if (route.path === itemUrls.value.display) {
                    // @ts-ignore - monkeypatched router, drop with migration.
                    router.push(itemUrls.value.display, { title: props.name, force: true });
                }
                else if (itemUrls.value.display) {
                    // @ts-ignore - monkeypatched router, drop with migration.
                    router.push(itemUrls.value.display, { title: props.name });
                }
            }
        }
        function onDelete(recursive = false) {
            emit("delete", props.item, recursive);
            emit("update:selected", false);
            emit("init-key-selection");
        }
        function onUndelete() {
            emit("undelete");
            emit("update:selected", false);
            emit("init-key-selection");
        }
        function onDragStart(evt) {
            emit("drag-start", evt);
        }
        function onDragEnd() {
            clearDrag();
        }
        function onEdit() {
            router.push(itemUrls.value.edit);
        }
        function onShowCollectionInfo() {
            router.push(itemUrls.value.showDetails);
        }
        function onTags(newTags) {
            emit("tag-change", props.item, newTags);
            updateContentFields(props.item, { tags: newTags });
        }
        function onTagClick(tag) {
            if (props.filterable) {
                emit("tag-click", tag);
            }
        }
        function onButtonSelect(e) {
            var _a;
            const event = e;
            if (event.shiftKey) {
                onClick(e);
            }
            else {
                emit("init-key-selection");
            }
            (_a = contentItem.value) === null || _a === void 0 ? void 0 : _a.focus();
            emit("update:selected", !props.selected);
        }
        function toggleHighlights() {
            emit("toggleHighlights", props.item);
        }
        function unexpandedClick(event) {
            if (!props.expandDataset) {
                onClick(event);
            }
        }
        return { __sfc: true, router, route, props, emit, entryPointStore, eventStore, contentItem, subItemsVisible, jobState, contentId, contentCls, computedClass, contentState, hasTags, hasStateIcon, state, dataState, tags, tagsDisabled, isCollection, itemUrls, isSelectKey, onKeyDown, onClick, onDisplay, onDelete, onUndelete, onDragStart, onDragEnd, onEdit, onShowCollectionInfo, onTags, onTagClick, onButtonSelect, toggleHighlights, unexpandedClick, faCheckSquare, faSquare, faArrowCircleDown, faArrowCircleUp, faCheckCircle, faExchangeAlt, FontAwesomeIcon, BBadge, BButton, BCollapse, CollectionDescription, ContentOptions, DatasetDetails, StatelessTags };
    }
});
